"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdMachinesModal = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const CustomModal_1 = require("../../../components/custom-modal/CustomModal");
const useAdvertising_1 = require("../context/useAdvertising");
const useToast_1 = require("../../../hooks/toast/useToast");
const advertising_module_scss_1 = __importDefault(require("../advertising.module.scss"));
const PAGE_SIZE = 10;
const AdMachinesModal = ({ visible, adId, onHide, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const { getAdMachines } = (0, useAdvertising_1.useAdvertising)();
    const { showError } = (0, useToast_1.useToast)();
    const [machines, setMachines] = (0, react_1.useState)([]);
    const [totalItems, setTotalItems] = (0, react_1.useState)(0);
    const [currentPage, setCurrentPage] = (0, react_1.useState)(1);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const getAdMachinesRef = (0, react_1.useRef)(getAdMachines);
    const showErrorRef = (0, react_1.useRef)(showError);
    const tRef = (0, react_1.useRef)(t);
    (0, react_1.useEffect)(() => {
        getAdMachinesRef.current = getAdMachines;
        showErrorRef.current = showError;
        tRef.current = t;
    });
    (0, react_1.useEffect)(() => {
        if (!visible || !adId)
            return;
        let cancelled = false;
        const loadFirstPage = async () => {
            setLoading(true);
            try {
                const res = await getAdMachinesRef.current(adId, 1, PAGE_SIZE);
                if (cancelled)
                    return;
                setMachines(res.items);
                setTotalItems(res.pagination?.totalItems ?? res.items.length);
                setCurrentPage(1);
            }
            catch {
                if (cancelled)
                    return;
                showErrorRef.current(tRef.current('advertising.ads.machinesModal.loadError'));
                setMachines([]);
                setTotalItems(0);
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        };
        loadFirstPage();
        return () => {
            cancelled = true;
        };
    }, [visible, adId]);
    const hasMore = machines.length < totalItems;
    const isInitialLoading = loading && machines.length === 0;
    const isEmpty = !loading && machines.length === 0;
    const loadMore = (0, react_1.useCallback)(async () => {
        if (!adId || loading || !hasMore)
            return;
        const nextPage = currentPage + 1;
        setLoading(true);
        try {
            const res = await getAdMachinesRef.current(adId, nextPage, PAGE_SIZE);
            setMachines((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                return [...prev, ...res.items.filter((m) => !seen.has(m.id))];
            });
            setTotalItems((prev) => res.pagination?.totalItems ?? prev);
            setCurrentPage(nextPage);
        }
        catch {
            showErrorRef.current(tRef.current('advertising.ads.machinesModal.loadError'));
        }
        finally {
            setLoading(false);
        }
    }, [adId, loading, hasMore, currentPage]);
    const loadMoreRef = (0, react_1.useRef)(loadMore);
    (0, react_1.useEffect)(() => {
        loadMoreRef.current = loadMore;
    });
    const sentinelRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        if (!visible || !hasMore)
            return;
        const sentinel = sentinelRef.current;
        if (!sentinel)
            return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting)
                loadMoreRef.current();
        }, { rootMargin: '40px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [visible, hasMore, machines.length]);
    return ((0, jsx_runtime_1.jsxs)(CustomModal_1.CustomModal, { visible: visible, onHide: onHide, title: t('advertising.ads.machinesModal.title'), className: advertising_module_scss_1.default.adMachinesModal, children: [totalItems > 0 && ((0, jsx_runtime_1.jsx)("p", { className: advertising_module_scss_1.default.adMachinesSubtitle, children: t('advertising.ads.machinesModal.subtitle', { count: totalItems }) })), (0, jsx_runtime_1.jsx)("div", { className: advertising_module_scss_1.default.adMachineScroll, children: isInitialLoading ? ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerEmpty, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerEmptyIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-spin pi-spinner" }) }), (0, jsx_runtime_1.jsx)("p", { children: t('advertising.ads.machinesModal.loading') })] })) : isEmpty ? ((0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.pickerEmpty, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.pickerEmptyIcon, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-inbox" }) }), (0, jsx_runtime_1.jsx)("p", { children: t('advertising.ads.machinesModal.empty') })] })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("ul", { className: advertising_module_scss_1.default.adMachineList, children: machines.map((machine) => ((0, jsx_runtime_1.jsxs)("li", { className: advertising_module_scss_1.default.adMachineRow, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMachineAvatar, "aria-hidden": true, children: (0, jsx_runtime_1.jsx)("i", { className: "pi pi-server" }) }), (0, jsx_runtime_1.jsxs)("div", { className: advertising_module_scss_1.default.adMachineMain, children: [(0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMachineName, title: machine.name, children: machine.name }), (0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachineSite, title: machine.siteName ||
                                                    t('advertising.ads.machinesModal.siteUnknown'), children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-map-marker", "aria-hidden": true }), machine.siteName ||
                                                        t('advertising.ads.machinesModal.siteUnknown')] })] }), machine.code && ((0, jsx_runtime_1.jsx)("span", { className: advertising_module_scss_1.default.adMachineCode, children: machine.code }))] }, machine.id))) }), hasMore && ((0, jsx_runtime_1.jsx)("div", { ref: sentinelRef, className: advertising_module_scss_1.default.adMachineSentinel, children: loading && ((0, jsx_runtime_1.jsxs)("span", { className: advertising_module_scss_1.default.adMachineLoadingMore, "aria-live": "polite", children: [(0, jsx_runtime_1.jsx)("i", { className: "pi pi-spin pi-spinner", "aria-hidden": true }), t('advertising.ads.machinesModal.loading')] })) }))] })) })] }));
};
exports.AdMachinesModal = AdMachinesModal;
